import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import ProductsSection from '../components/ProductsSection';

const ProductsPage = () => {
    return (
        <div className="min-h-screen bg-black">
            <SEO
                title="Our Products - Qivra Technologies | Enterprise Solutions"
                description="Explore QivraCampus AI and other enterprise-grade products built with cutting-edge technology. Transform your business with our production-ready solutions."
                keywords="enterprise software, school management system, QivraCampus AI, business solutions, SaaS products, AI automation"
            />

            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-32 pb-20">
                {/* Background Effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
                    <div
                        className="absolute inset-0 opacity-20"
                        style={{
                            backgroundImage: `
                radial-gradient(circle at 20% 80%, rgba(34, 211, 238, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(14, 165, 233, 0.1) 0%, transparent 50%)
              `
                        }}
                    />
                </div>

                <motion.div
                    className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
                        <span className="text-cyan-400 text-sm font-light tracking-wide">Product Portfolio</span>
                    </div>

                    <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-thin mb-6 leading-tight tracking-tight">
                        <span className="text-white font-extralight">
                            Our Products
                        </span>
                    </h1>

                    <div className="mb-12">
                        <span className="text-2xl sm:text-3xl md:text-4xl font-light tracking-widest bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent">
                            Replace Legacy, Embrace the Future
                        </span>
                    </div>

                    <p className="text-xl text-gray-300 mb-16 max-w-3xl mx-auto font-light leading-relaxed">
                        Next-generation enterprise products designed to completely replace outdated systems. Built with cutting-edge AI and modern architecture to transform your operations and unlock unprecedented efficiency.
                    </p>
                </motion.div>
            </section>

            {/* Products Section */}
            <ProductsSection />

            {/* Why Choose Our Products Section */}
            <section className="py-32 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage: `
              radial-gradient(circle at 30% 70%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 70% 30%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)
            `
                    }}
                />

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                        <h2 className="text-5xl md:text-6xl font-extralight mb-8 leading-tight tracking-wide">
                            <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent">
                                Why Upgrade to Next-Gen?
                            </span>
                        </h2>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-10">
                        {[
                            {
                                title: 'Replace Legacy Systems',
                                description: 'Completely replace outdated, inefficient school management systems with modern, AI-powered solutions that eliminate manual processes and unlock new capabilities.',
                                gradient: 'from-cyan-400 to-blue-500'
                            },
                            {
                                title: 'AI-Powered Intelligence',
                                description: 'Next-generation automation with computer vision, natural language processing, and intelligent workflows that legacy systems cannot provide.',
                                gradient: 'from-purple-400 to-pink-500'
                            },
                            {
                                title: 'Seamless Migration',
                                description: 'Zero-downtime transition from your old system. Enterprise-grade architecture ensures smooth migration with full data integrity and immediate productivity gains.',
                                gradient: 'from-green-400 to-teal-500'
                            }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: idx * 0.15 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -8, scale: 1.02 }}
                                className="group relative bg-gradient-to-b from-gray-900/50 to-black/80 backdrop-blur-sm p-10 rounded-3xl border border-gray-800/60 hover:border-cyan-400/40 transition-all duration-500 overflow-hidden"
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

                                <div className="relative z-10">
                                    <h3 className="text-2xl font-light text-white mb-6 group-hover:text-cyan-300 transition-colors duration-300 tracking-wide">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-300 leading-relaxed font-light tracking-wide">
                                        {item.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductsPage;
